import { createStore } from 'zustand/vanilla';
import { parseBrowserPresences, type BrowserPresence } from './browserAutoLink';

/** UI hint only. It never grants Browser access or suppresses advertising. */
export const nativeToolsStore = createStore<{
  connected: boolean | null;
  setConnected(value: boolean | null): void;
}>(set => ({ connected: null, setConnected: connected => set({ connected }) }));

interface Options {
  origin: string;
  discover(signal: AbortSignal): Promise<unknown>;
  fetch: typeof fetch;
  onChange(connected: boolean | null): void;
}

function abortable<T>(work: Promise<T>, signal: AbortSignal): Promise<T> {
  return new Promise((resolve, reject) => {
    const abort = () => {
      signal.removeEventListener('abort', abort);
      reject(new DOMException('Presence request cancelled', 'AbortError'));
    };
    if (signal.aborted) { abort(); return; }
    signal.addEventListener('abort', abort, { once: true });
    void work.then(value => {
      signal.removeEventListener('abort', abort);
      resolve(value);
    }, error => {
      signal.removeEventListener('abort', abort);
      reject(error);
    });
  });
}

async function readChallenge(response: Response): Promise<unknown> {
  const reader = response.body?.getReader();
  if (!reader) throw new Error('Missing local challenge');
  const decoder = new TextDecoder();
  let bytes = 0, text = '';
  try {
    while (true) {
      const part = await reader.read();
      if (part.done) break;
      bytes += part.value.byteLength;
      if (bytes > 4096) throw new Error('Oversized local challenge');
      text += decoder.decode(part.value, { stream: true });
    }
    return JSON.parse(text + decoder.decode());
  } finally {
    await reader.cancel().catch(() => undefined);
    reader.releaseLock();
  }
}

/** Call from Studio's authorized origin, through the existing page-local portal client. */
export function watchToolsPresence(options: Options): { dispose(): void } {
  let disposed = false;
  let request: AbortController | null = null;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let expiry: ReturnType<typeof setTimeout> | undefined;

  function publish(connected: boolean | null, until = 0): void {
    if (disposed) return;
    if (expiry) clearTimeout(expiry);
    options.onChange(connected);
    if (connected !== null) expiry = setTimeout(() => {
      if (!disposed) options.onChange(null);
    }, Math.max(0, until - Date.now()));
  }

  async function probe(presence: BrowserPresence, signal: AbortSignal): Promise<boolean | null> {
    try {
      const response = await options.fetch(`http://127.0.0.1:${presence.port}/v1/browser/challenge`, {
        credentials: 'omit', redirect: 'error', cache: 'no-store', signal,
      });
      if (!response.ok) { await response.body?.cancel(); return null; }
      const value = await readChallenge(response);
      if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
      const body = value as Record<string, unknown>;
      if (Object.keys(body).length !== 4 || body.version !== 2 ||
          typeof body.instanceId !== 'string' || typeof body.challenge !== 'string' ||
          !Array.isArray(body.features) || body.features.length !== 1 || body.features[0] !== 'browser_auto_link_v2') return null;
      return body.instanceId === presence.instanceId && body.challenge === presence.challenge;
    } catch { return null; }
  }

  async function poll(): Promise<void> {
    const controller = new AbortController();
    request = controller;
    const timeout = setTimeout(() => controller.abort(), 4000);
    try {
      const raw = await abortable(options.discover(controller.signal), controller.signal);
      if (!Array.isArray(raw) || raw.length > 8) throw new Error('Invalid Tools discovery');
      const local = raw.filter(value => value && typeof value === 'object' && value.studioOrigin === options.origin);
      const candidates = parseBrowserPresences(local, options.origin).filter(p => p.features[0] === 'browser_auto_link_v2');
      const matches: Array<boolean | null> = [];
      for (let index = 0; index < candidates.length; index += 2) {
        if (controller.signal.aborted || disposed) return;
        const batch = await abortable(Promise.all(candidates.slice(index, index + 2).map(p => probe(p, controller.signal))), controller.signal);
        matches.push(...batch);
      }
      if (disposed || controller.signal.aborted) return;
      const verifiedUntil = Math.max(0, ...candidates.map((p, index) => matches[index] === true ? Date.parse(p.expiresAt) : 0));
      if (verifiedUntil > Date.now()) publish(true, verifiedUntil);
      // Browser-disabled Tools does not announce Browser presence. Neither an empty
      // list nor a different local instance establishes that Tools is absent.
      else publish(null);
    } catch {
      if (!disposed) publish(null);
    } finally {
      clearTimeout(timeout);
      if (request === controller) request = null;
      if (!disposed) timer = setTimeout(() => { void poll(); }, 5000);
    }
  }

  void poll();
  return { dispose() {
    disposed = true;
    request?.abort();
    if (timer) clearTimeout(timer);
    if (expiry) clearTimeout(expiry);
  } };
}
